import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { GlobalSearchResult, LoadDetails, LoadsService } from '../loads.service';
import { BehaviorSubject, combineLatestWith, lastValueFrom, Observable } from 'rxjs';
import { map, shareReplay, take } from 'rxjs/operators';
import { shareReplayComponentConfig } from '../../../shared/constants';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

@Component({
  selector: 'vst-load-search',
  templateUrl: './load-search.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadSearchComponent {
  public searchBox = new FormControl<string>(null);
  public searchResults$: Observable<GlobalSearchResult[]>;

  private maxLoadsToShow$$ = new BehaviorSubject<number>(5);
  public maxLoadsToShow$ = this.maxLoadsToShow$$.pipe(shareReplay(shareReplayComponentConfig));
  public maxLoadSearchResults$: Observable<{
    results: GlobalSearchResult[];
    showMore: boolean;
  }>;
  public active$: Observable<boolean>;
  public firstResult$: Observable<GlobalSearchResult>;

  constructor(
    private loadService: LoadsService,
    public ref: MatDialogRef<LoadSearchComponent>,
    private router: Router,
  ) {
    this.searchResults$ = this.loadService.searchResults$;
    this.active$ = this.loadService.loading$;
    this.searchBox.valueChanges.subscribe({
      next: (value) => this.loadService.search(value),
    });

    this.maxLoadSearchResults$ = this.searchResults$.pipe(
      combineLatestWith(this.maxLoadsToShow$),
      map(([results, max]) => {
        return {
          results: results.slice(0, max),
          showMore: results.length > max,
        };
      }),
    );

    this.firstResult$ = this.searchResults$.pipe(
      map((results) => results[0]),
      shareReplay(shareReplayComponentConfig),
    );
  }

  public showMoreLoads() {
    this.maxLoadsToShow$$.next(this.maxLoadsToShow$$.value + 5);
  }

  @HostListener('document:keydown', ['$event'])
  public async onKeyDown(event: KeyboardEvent) {
    if (event.key.toLowerCase() === 'tab') {
      return;
    }

    if ((event.target as HTMLElement).tagName === 'INPUT') {
      if (event.key.toLowerCase() === 'enter') {
        this.handleEnter();
        return;
      }
      return;
    }

    if (event.key.toLowerCase() === 'enter') {
      if (this.searchBox.pristine) {
        this.handleEnter();
        return;
      }
      return;
    }
  }

  private async handleEnter() {
    const firstResult = await lastValueFrom(this.firstResult$.pipe(take(1)));
    if (firstResult) {
      this.ref.close();
      this.router.navigate(['/loads', firstResult.id]);
    }
  }
}
